import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Paper } from '@mui/material';
import { supabase } from '../lib/supabase';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Processing authentication...');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get params from both query string (?) and hash fragment (#)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));

        const code = searchParams.get('code') || hashParams.get('code');
        const access_token = searchParams.get('access_token') || hashParams.get('access_token');
        const refresh_token = searchParams.get('refresh_token') || hashParams.get('refresh_token');
        const error_description = searchParams.get('error_description') || hashParams.get('error_description');
        const error_code = searchParams.get('error_code') || hashParams.get('error_code');
        const type = searchParams.get('type') || hashParams.get('type');

        console.log('Auth callback params:', {
          code: !!code,
          access_token: !!access_token,
          refresh_token: !!refresh_token,
          type,
          error_code,
          error_description,
        });

        // Check for errors in URL
        if (error_description || error_code) {
          let errorMessage = error_description || 'Authentication failed';

          // Provide helpful messages for common errors
          if (error_code === 'otp_expired' || errorMessage.includes('expired')) {
            errorMessage = 'This login link has expired. Please request a new one using the "Resend" button in the admin panel.';
          } else if (error_code === 'access_denied') {
            errorMessage = 'Access denied. ' + errorMessage;
          }

          setError(errorMessage);
          setStatus('Authentication failed');

          // Redirect to login after showing error
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 5000);
          return;
        }

        // If we have tokens in the URL (magic link flow)
        if (access_token && refresh_token) {
          setStatus('Authenticating with magic link...');

          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token,
            refresh_token,
          });

          if (sessionError) {
            console.error('Error setting session:', sessionError);
            setError(sessionError.message);
            setStatus('Authentication failed');
            return;
          }

          console.log('Session set successfully:', data.user?.email);
          console.log('User metadata:', data.user);

          // Check if user needs to set password
          // For invited users via inviteUserByEmail, they don't have a password until they set one
          // We check: 1) If this is marked as invite type, 2) If user has password_set metadata
          const needsPasswordSetup =
            type === 'invite' ||
            type === 'signup' ||
            data.user?.user_metadata?.needs_password_setup === true ||
            !data.user?.user_metadata?.password_set;

          console.log('Needs password setup?', needsPasswordSetup);

          if (needsPasswordSetup) {
            setStatus('Redirecting to password setup...');
            setTimeout(() => {
              navigate('/set-password', { replace: true });
            }, 1000);
          } else {
            setStatus('Redirecting to dashboard...');
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 1000);
          }
          return;
        }

        // If we have a code (OAuth or email verification flow)
        if (code) {
          setStatus('Exchanging code for session...');

          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('Error exchanging code:', exchangeError);
            setError(exchangeError.message);
            setStatus('Authentication failed');
            return;
          }

          console.log('Code exchanged successfully:', data.user?.email);
          console.log('User metadata:', data.user);

          // Check if user needs to set password
          const needsPasswordSetup =
            type === 'invite' ||
            type === 'signup' ||
            data.user?.user_metadata?.needs_password_setup === true ||
            !data.user?.user_metadata?.password_set;

          console.log('Needs password setup?', needsPasswordSetup);

          if (needsPasswordSetup) {
            setStatus('Redirecting to password setup...');
            setTimeout(() => {
              navigate('/set-password', { replace: true });
            }, 1000);
          } else {
            setStatus('Redirecting to dashboard...');
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 1000);
          }
          return;
        }

        // If we have type=recovery (password reset)
        if (type === 'recovery') {
          setStatus('Password reset verified. Redirecting...');

          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1000);
          return;
        }

        // No valid auth params found
        console.warn('No valid auth parameters found in URL');
        setError('No authentication parameters found in URL');
        setStatus('Authentication failed');

        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);

      } catch (err: any) {
        console.error('Unexpected error in auth callback:', err);
        setError(err.message || 'An unexpected error occurred');
        setStatus('Authentication failed');

        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Redirecting to login page...
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              {status}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we complete your authentication.
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
}
